import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { formatApiErrorDetail } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import DateOfBirthPicker from "@/components/DateOfBirthPicker";

export default function Register() {
  const nav = useNavigate();
  const loc = useLocation();
  const role = loc.state?.role || "farmer";
  const theme = ROLE_THEMES[role];
  const { register } = useAuth();
  const { t } = useI18n();

  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", dob: "", state: "", district: "", village: "", gender: "", password: "", confirm: "", referral_code: "",
  });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sp] = useSearchParams();

  useEffect(() => {
    const ref = sp.get("ref");
    if (ref) setForm((f) => ({ ...f, referral_code: ref.toUpperCase() }));
  }, [sp]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!terms) return toast.error("Please accept the terms");
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    if (!form.full_name || !form.mobile || !form.password) return toast.error("Fill required fields");
    setBusy(true);
    try {
      const { confirm, email, referral_code, ...rest } = form;
      const payload = { ...rest, role };
      if (email) payload.email = email;
      if (referral_code && referral_code.trim()) payload.referral_code = referral_code.trim().toUpperCase();
      await register(payload);
      nav("/welcome");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7] flex">
      <div className="hidden lg:block w-[42%] relative overflow-hidden">
        <img src={theme.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-90`} />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="text-xs tracking-[0.24em] uppercase opacity-90">Joining as</div>
          <div>
            <div className="text-5xl font-bold tracking-tight">{theme.name}</div>
            <p className="mt-4 text-white/85 max-w-sm leading-relaxed">Your dashboard is being prepared. Fill in your details to unlock role-specific opportunities.</p>
          </div>
          <div className="text-white/70 text-sm">Grow Together Farmily · 2026</div>
        </div>
      </div>

      <div className="flex-1 px-6 md:px-12 py-10 overflow-y-auto">
        <div className="max-w-xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="text-xs font-semibold tracking-[0.24em] uppercase text-emerald-700">{t("auth.step02")}</div>
            <LanguageSwitcher />
          </div>
          <h1 className="mt-2 text-4xl md:text-5xl font-bold tracking-tight text-gray-900">{t("register.title")}</h1>
          <p className="mt-2 text-gray-600">{t("register.joining_as")} <b className={theme.text}>{theme.name}</b>. <button onClick={() => nav("/role")} className="text-emerald-700 underline">{t("register.change")}</button></p>

          <motion.form initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("register.full_name")} tid={AUTH.nameInput} value={form.full_name} onChange={set("full_name")} />
            <Field label={t("register.mobile")} tid={AUTH.mobileInput} value={form.mobile} onChange={set("mobile")} />
            <Field label={t("register.email")} tid={AUTH.emailInput} type="email" value={form.email} onChange={set("email")} />
            <div>
              <label className="text-xs font-semibold text-gray-600">{t("register.dob")}</label>
              <div className="mt-1"><DateOfBirthPicker value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} testid={AUTH.dobInput} /></div>
            </div>
            <Field label={t("register.state")} tid={AUTH.stateInput} value={form.state} onChange={set("state")} />
            <Field label={t("register.district")} tid={AUTH.districtInput} value={form.district} onChange={set("district")} />
            <Field label={t("register.village")} tid={AUTH.villageInput} value={form.village} onChange={set("village")} />
            <div>
              <label className="text-xs font-semibold text-gray-600 tracking-wide">{t("register.gender")}</label>
              <select data-testid={AUTH.genderInput} value={form.gender} onChange={set("gender")}
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500">
                <option value="">{t("register.select")}</option><option>{t("register.male")}</option><option>{t("register.female")}</option><option>{t("register.other")}</option>
              </select>
            </div>
            <Field label={t("register.password")} tid={AUTH.passwordInput} type="password" value={form.password} onChange={set("password")} />
            <Field label={t("register.confirm")} tid={AUTH.confirmInput} type="password" value={form.confirm} onChange={set("confirm")} />

            <div className="md:col-span-2">
              <label className="text-xs font-semibold text-gray-600 tracking-wide">Referral Code (optional)</label>
              <input data-testid="register-referral" value={form.referral_code} onChange={(e) => setForm({ ...form, referral_code: e.target.value.toUpperCase() })}
                placeholder="Have a code? Enter it to get ₹50 after your first order"
                className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono tracking-widest" />
            </div>

            <label className="col-span-full mt-2 flex items-center gap-3 text-sm text-gray-700">
              <input data-testid={AUTH.termsInput} type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="h-4 w-4 rounded" />
              {t("register.accept_terms")}
            </label>

            <button data-testid={AUTH.registerSubmit} disabled={busy}
              className={`col-span-full mt-4 px-8 py-4 rounded-full font-semibold text-white ${theme.bg} hover:opacity-95 shadow-xl disabled:opacity-60`}>
              {busy ? t("register.creating") : t("register.create")}
            </button>

            <div className="col-span-full text-center text-sm text-gray-600">
              {t("register.have_account")} <Link data-testid={AUTH.toLogin} to="/login" className="text-emerald-700 font-medium">{t("auth.signin")}</Link>
            </div>
          </motion.form>
        </div>
      </div>
    </div>
  );
}

function Field({ label, tid, ...rest }) {
  return (
    <div>
      <label className="text-xs font-semibold text-gray-600 tracking-wide">{label}</label>
      <input data-testid={tid} {...rest}
        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-4 py-3 focus:outline-none focus:ring-2 focus:ring-emerald-500" />
    </div>
  );
}
